import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { ArrowLeft, ArrowRight, Lock, Loader2, CreditCard, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import PricingCard from "../../components/wealth/PricingCard";
import ToolkitDownloads from "../../components/wealth/ToolkitDownloads";
import { formatUSD, LAUNCH_PRICE, CTA_BUTTON_LABEL, CTA_SUBTEXT } from "../../lib/wealthPricing";

const ASSET_RANGES = [
    { v: "under_100k", label: "Less than $100,000" },
    { v: "100k_250k", label: "$100,000 – $250,000" },
    { v: "250k_500k", label: "$250,000 – $500,000" },
    { v: "500k_1m", label: "$500,000 – $1 million" },
    { v: "1m_2m", label: "$1 million – $2 million" },
    { v: "over_2m", label: "Over $2 million" },
];

const CONFIDENCE = ["Very confident", "Confident", "Somewhat confident", "Not confident", "No idea"];
const RISK = ["Not worried", "Slightly worried", "Worried", "Very worried"];

const LTC_OPTIONS = [
    { v: "yes_funded", label: "Yes — I have a funded plan (LTC policy, hybrid, or earmarked assets)" },
    { v: "yes_partial", label: "Partially — some coverage but I know there's a gap" },
    { v: "no_concerned", label: "No, and I'm worried about it" },
    { v: "no_not_concerned", label: "No, but I'm not concerned" },
    { v: "unsure", label: "I'm not sure" },
];

const PRIORITIES = [
    { v: "income", label: "Income I can't outlive" },
    { v: "growth", label: "Growing my savings" },
    { v: "protection", label: "Protecting what I have from market drops" },
    { v: "legacy", label: "Leaving a legacy" },
];

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const AGE_RANGES = ["Under 45", "45-54", "55-59", "60-64", "65-69", "70-74", "75-79", "80+"];

const TOTAL_STEPS = 6;

export default function Assessment() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const fromToolkit = params.get("from_toolkit");
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [prefilling, setPrefilling] = useState(Boolean(fromToolkit));
    const [prefillError, setPrefillError] = useState(null);
    const [data, setData] = useState({
        full_name: "",
        email: "",
        phone: "",
        age: "",
        state: "",
        zip_code: "",
        quiz: {
            retirement_status: "",
            asset_range: "",
            owns_annuity: "",
            annuity_type: "",
            owns_cd: "",
            income_confidence: "",
            market_risk: "",
            ltc_plan: "",
            top_priority: "",
        },
    });

    // Toolkit-buyer flow: fetch lead pre-fill data (gated server-side on
    // wealth_report_purchased=true). If they already completed the assessment,
    // bounce them straight to the results page — never re-collect a paid quiz.
    useEffect(() => {
        if (!fromToolkit) return;
        let cancelled = false;
        (async () => {
            try {
                const { data: lead } = await api.get(`/wealth/from-toolkit-info/${fromToolkit}`);
                if (cancelled) return;
                if (lead.wealth_quiz && lead.wealth_report_score) {
                    navigate(`/wealth/results?lead_id=${fromToolkit}`, { replace: true });
                    return;
                }
                setData((p) => ({
                    ...p,
                    full_name: lead.full_name || p.full_name,
                    email: lead.email || p.email,
                    phone: lead.phone || p.phone,
                    age: lead.age || p.age,
                    state: lead.state || p.state,
                    zip_code: lead.zip_code || p.zip_code,
                }));
                setPrefilling(false);
                // Fire-and-forget tracking — drives the "Assessment Started" metric
                // and stops the recovery-email loop from nudging this lead twice.
                api.post(`/wealth/assessment-started/${fromToolkit}`).catch(() => {});
            } catch (e) {
                if (!cancelled) {
                    setPrefillError(formatApiError(e));
                    setPrefilling(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [fromToolkit, navigate]);

    const set = (k, v) => setData((p) => ({ ...p, [k]: v }));
    const setQ = (k, v) => setData((p) => ({ ...p, quiz: { ...p.quiz, [k]: v } }));

    const canNext = useMemo(() => {
        if (step === 1) return data.full_name.trim() && data.email.includes("@") && data.phone.trim();
        if (step === 2) return data.age && data.state && data.zip_code.trim();
        if (step === 3) return data.quiz.retirement_status && data.quiz.owns_annuity && data.quiz.asset_range;
        if (step === 4) return data.quiz.owns_cd && (data.quiz.owns_annuity !== "Yes" || data.quiz.annuity_type);
        if (step === 5) return data.quiz.income_confidence && data.quiz.market_risk && data.quiz.ltc_plan;
        if (step === 6) return data.quiz.top_priority;
        return false;
    }, [step, data]);

    async function onCheckout() {
        if (!canNext) return;
        setSubmitting(true);
        try {
            const sp = new URLSearchParams(window.location.search);
            const payload = {
                ...data,
                origin_url: window.location.origin,
                lead_source: sp.get("utm_source") || sp.get("src") || "direct",
                lead_source_detail: sp.get("utm_campaign") || sp.get("utm_content") || null,
            };
            const { data: res } = await api.post("/wealth/start", payload);
            if (res?.checkout_url) {
                window.location.href = res.checkout_url;
            } else {
                toast.error("Could not start checkout. Please try again.");
                setSubmitting(false);
            }
        } catch (e) {
            toast.error(formatApiError(e));
            setSubmitting(false);
        }
    }

    // Toolkit-buyer submit: NO Stripe call — payment was made via /start-from-lead.
    // Sends the quiz + any contact corrections to /complete-from-toolkit, which
    // computes the score, sends the email, and updates the CRM, then routes to results.
    async function onCompleteToolkit() {
        if (!canNext || !fromToolkit) return;
        setSubmitting(true);
        try {
            const payload = {
                lead_id: fromToolkit,
                quiz: data.quiz,
                full_name: data.full_name,
                email: data.email,
                phone: data.phone,
                age: data.age,
                state: data.state,
                zip_code: data.zip_code,
            };
            const { data: res } = await api.post("/wealth/complete-from-toolkit", payload);
            navigate(`/wealth/results?lead_id=${fromToolkit}`, {
                replace: true,
                state: { score: res?.score, leadId: fromToolkit, name: data.full_name },
            });
        } catch (e) {
            toast.error(formatApiError(e));
            setSubmitting(false);
        }
    }

    if (prefilling) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="toolkit-prefill-loading">
                    <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl text-emerald-950 font-semibold">Loading your assessment…</h2>
                    <p className="mt-3 text-sm text-emerald-900">Confirming your Toolkit purchase and pre-filling your details.</p>
                </div>
            </div>
        );
    }
    if (prefillError) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="toolkit-prefill-error">
                    <h2 className="font-serif text-2xl text-emerald-950 font-semibold">We couldn't load your assessment</h2>
                    <p className="mt-3 text-sm text-emerald-900">{prefillError}</p>
                    <p className="mt-4 text-xs text-emerald-900">If you just paid, give it a moment and refresh. Otherwise email reviews@yourannuityreview.com.</p>
                </div>
            </div>
        );
    }

    // Toolkit-buyer layout: condensed to just TWO logical screens.
    //   Step 1 — Contact Information (pre-filled & editable, all fields one screen)
    //   Step 2 — Retirement Assessment Questions (all quiz Qs one scrollable screen)
    // The standalone $29.95 buyer continues with the 6-step funnel below.
    if (fromToolkit) {
        return (
            <ToolkitTwoStep
                data={data}
                set={set}
                setQ={setQ}
                submitting={submitting}
                onSubmit={onCompleteToolkit}
                navigate={navigate}
                leadId={fromToolkit}
            />
        );
    }

    return (
        <div className="min-h-[80vh] bg-[#fdfbf6] py-12 px-4" data-testid="wealth-assessment">
            <div className="max-w-2xl mx-auto">
                {/* Progress */}
                <div className="mb-8">
                    <div className="flex items-center justify-between text-xs text-emerald-900 font-medium tracking-wide">
                        <span data-testid="assessment-step-indicator">Step {step} of {TOTAL_STEPS}</span>
                        {fromToolkit ? (
                            <span className="flex items-center gap-1.5 text-emerald-700 font-semibold" data-testid="toolkit-mode-banner"><FileCheck2 className="w-3.5 h-3.5" /> Toolkit purchased · No additional charge</span>
                        ) : (
                            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Secure · {formatUSD(LAUNCH_PRICE)} charged after final step</span>
                        )}
                    </div>
                    <div className="mt-2 h-1.5 bg-emerald-900/10 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-700 transition-all" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
                    </div>
                </div>

                <div className="bg-white border border-emerald-900/10 rounded-2xl p-8 sm:p-10 shadow-sm">
                    {step === 1 && (
                        <Section title="Let's start with you" subtitle="We need your name & email to deliver your PDF-style report.">
                            <Field label="Full name" required>
                                <input data-testid="field-full-name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} placeholder="Jane Smith" />
                            </Field>
                            <Field label="Email" required>
                                <input data-testid="field-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="jane@example.com" />
                            </Field>
                            <Field label="Phone" required>
                                <input data-testid="field-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="(555) 555-5555" />
                            </Field>
                        </Section>
                    )}

                    {step === 2 && (
                        <Section title="Where are you in life?" subtitle="State helps us match the right advisor if you want the complimentary follow-up review.">
                            <Field label="Age range" required>
                                <select data-testid="field-age" value={data.age} onChange={(e) => set("age", e.target.value)} className={inputCls}>
                                    <option value="">Select…</option>
                                    {AGE_RANGES.map((a) => <option key={a}>{a}</option>)}
                                </select>
                            </Field>
                            <Field label="State" required>
                                <select data-testid="field-state" value={data.state} onChange={(e) => set("state", e.target.value)} className={inputCls}>
                                    <option value="">Select…</option>
                                    {STATES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="ZIP code" required>
                                <input data-testid="field-zip" value={data.zip_code} onChange={(e) => set("zip_code", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} className={inputCls} placeholder="74103" />
                            </Field>
                        </Section>
                    )}

                    {step === 3 && (
                        <Section title="The big picture" subtitle="Approximate ranges are fine — this drives the Growth Strength sub-score.">
                            <Choice label="Are you currently retired or planning to?" name="retirement_status" value={data.quiz.retirement_status} onChange={(v) => setQ("retirement_status", v)}
                                options={[
                                    { v: "retired", label: "I'm retired" },
                                    { v: "planning", label: "Planning to retire in 1–10 years" },
                                    { v: "working", label: "Still working, retirement is further out" },
                                ]} />
                            <Choice label="Do you currently own an annuity?" name="owns_annuity" value={data.quiz.owns_annuity} onChange={(v) => setQ("owns_annuity", v)}
                                options={[
                                    { v: "Yes", label: "Yes" },
                                    { v: "No", label: "No" },
                                    { v: "Not Sure", label: "Not Sure" },
                                ]} />
                            <Choice label="Total retirement savings (all accounts combined)" name="asset_range" value={data.quiz.asset_range} onChange={(v) => setQ("asset_range", v)} options={ASSET_RANGES} />
                        </Section>
                    )}

                    {step === 4 && (
                        <Section title="What else do you own?" subtitle="Tells us how exposed you are to specific product risks.">
                            {data.quiz.owns_annuity === "Yes" && (
                                <Choice label="What type of annuity do you own?" name="annuity_type" value={data.quiz.annuity_type} onChange={(v) => setQ("annuity_type", v)}
                                    options={[
                                        { v: "Variable", label: "Variable annuity" },
                                        { v: "Fixed Indexed", label: "Fixed indexed annuity (FIA)" },
                                        { v: "Fixed", label: "Fixed / MYGA annuity" },
                                        { v: "Not sure", label: "I'm not sure what type" },
                                    ]} />
                            )}
                            {data.quiz.owns_annuity !== "Yes" && (
                                <div className="p-4 rounded-lg bg-emerald-50/60 border border-emerald-200/70 text-sm text-emerald-900/85" data-testid="annuity-skip-note">
                                    No annuity ownership noted — we'll skip the annuity-specific questions.
                                </div>
                            )}
                            <Choice label="Do you have money sitting in CDs or savings earning low rates?" name="owns_cd" value={data.quiz.owns_cd} onChange={(v) => setQ("owns_cd", v)}
                                options={[{ v: "Yes", label: "Yes" }, { v: "No", label: "No" }]} />
                        </Section>
                    )}

                    {step === 5 && (
                        <Section title="How are you feeling about it?" subtitle="Be honest — these answers drive the protection & flexibility sub-scores.">
                            <Choice label="How confident are you in your retirement income?" name="income_confidence" value={data.quiz.income_confidence} onChange={(v) => setQ("income_confidence", v)}
                                options={CONFIDENCE.map((c) => ({ v: c, label: c }))} />
                            <Choice label="How worried are you about market downturns?" name="market_risk" value={data.quiz.market_risk} onChange={(v) => setQ("market_risk", v)}
                                options={RISK.map((r) => ({ v: r, label: r }))} />
                            <Choice label="Do you have a long-term care plan in place?" name="ltc_plan" value={data.quiz.ltc_plan} onChange={(v) => setQ("ltc_plan", v)} options={LTC_OPTIONS} />
                        </Section>
                    )}

                    {step === 6 && (
                        <Section title="One last question" subtitle="What matters most to you right now? We use this to weight your top recommendation.">
                            <Choice label="Your #1 retirement priority" name="top_priority" value={data.quiz.top_priority} onChange={(v) => setQ("top_priority", v)} options={PRIORITIES} />
                            <div className="mt-8 space-y-4">
                                {fromToolkit ? (
                                    <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="toolkit-finalize-note">
                                        <div className="flex items-start gap-3">
                                            <FileCheck2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-emerald-950 leading-relaxed">
                                                <strong>Toolkit purchased.</strong> Click <strong>Generate My Scorecard</strong> below to compute your score and email your printable report. <em>No additional charge.</em>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <PricingCard variant="compact" />
                                        <div className="p-4 rounded-lg bg-emerald-50/60 border border-emerald-200/70 text-sm text-emerald-900/85 leading-relaxed" data-testid="checkout-fineprint">
                                            Click <strong>Continue to Payment</strong> to securely complete your purchase via Stripe.
                                            Your PDF-style report is emailed instantly and your scorecard appears on the next screen.
                                        </div>
                                    </>
                                )}
                            </div>
                        </Section>
                    )}

                    {/* Nav */}
                    <div className="mt-10 flex items-center justify-between gap-4 pt-6 border-t border-emerald-900/10">
                        <button
                            onClick={() => (step === 1 ? navigate("/wealth") : setStep(step - 1))}
                            data-testid="assessment-back"
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-emerald-900 hover:text-emerald-900 text-sm font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {step < TOTAL_STEPS ? (
                            <button
                                disabled={!canNext}
                                onClick={() => setStep(step + 1)}
                                data-testid="assessment-next"
                                className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 disabled:bg-emerald-900/30 disabled:cursor-not-allowed text-amber-300 px-6 py-3 rounded-lg font-medium text-sm transition-colors"
                            >
                                Continue <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                disabled={!canNext || submitting}
                                onClick={fromToolkit ? onCompleteToolkit : onCheckout}
                                data-testid={fromToolkit ? "assessment-generate-scorecard" : "assessment-checkout"}
                                className="inline-flex items-center gap-2 bg-amber-300 hover:bg-amber-200 disabled:bg-amber-300/40 disabled:cursor-not-allowed text-emerald-950 px-6 py-3 rounded-lg font-bold text-sm shadow-md"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (fromToolkit ? <FileCheck2 className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
                                {submitting
                                    ? (fromToolkit ? "Generating your scorecard…" : "Redirecting to Stripe…")
                                    : (fromToolkit ? "Generate My Scorecard" : CTA_BUTTON_LABEL)}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full px-4 py-3 rounded-lg border border-emerald-900/15 bg-white text-emerald-950 placeholder:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/30 focus:border-emerald-700 transition";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block mb-1.5 text-sm font-medium text-emerald-900">{label}{required && <span className="text-amber-600"> *</span>}</span>
            {children}
        </label>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="space-y-5">
            <div>
                <h2 className="font-serif text-2xl sm:text-3xl text-emerald-950 font-semibold leading-tight">{title}</h2>
                {subtitle && <p className="mt-2 text-sm text-emerald-900 leading-relaxed">{subtitle}</p>}
            </div>
            <div className="space-y-5 pt-2">{children}</div>
        </div>
    );
}

function Choice({ label, name, value, onChange, options }) {
    return (
        <div>
            <div className="text-sm font-medium text-emerald-900 mb-3">{label}</div>
            <div className="grid sm:grid-cols-2 gap-2.5">
                {options.map((opt) => {
                    const active = value === opt.v;
                    return (
                        <button
                            key={opt.v}
                            type="button"
                            data-testid={`choice-${name}-${opt.v}`}
                            onClick={() => onChange(opt.v)}
                            className={`text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                active
                                    ? "border-emerald-700 bg-emerald-50 text-emerald-950 shadow-sm"
                                    : "border-emerald-900/10 bg-white text-emerald-900/80 hover:border-emerald-900/30 hover:bg-emerald-50/40"
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}


/**
 * Toolkit-buyer 2-step layout.
 * Step 1 — Contact Information (all 6 fields, pre-filled & editable, one screen)
 * Step 2 — Retirement Assessment Questions (all quiz questions, one scrollable screen)
 *
 * Lives in this file so it reuses the Field / Section / Choice helpers and inputCls
 * without duplication. Standalone $29.95 buyers keep the 6-step funnel.
 */
function ToolkitTwoStep({ data, set, setQ, submitting, onSubmit, navigate, leadId }) {
    const [step, setStep] = useState(1);
    const TOTAL = 2;

    const step1Valid =
        data.full_name.trim() &&
        data.email.includes("@") &&
        data.phone.trim() &&
        data.age &&
        data.state &&
        data.zip_code.trim();

    const q = data.quiz;
    const step2Valid =
        q.retirement_status &&
        q.owns_annuity &&
        q.asset_range &&
        q.owns_cd &&
        (q.owns_annuity !== "Yes" || q.annuity_type) &&
        q.income_confidence &&
        q.market_risk &&
        q.ltc_plan &&
        q.top_priority;

    return (
        <div className="min-h-[80vh] bg-[#fdfbf6] py-12 px-4" data-testid="wealth-assessment">
            <div className="max-w-2xl mx-auto">
                <div className="mb-8">
                    <div className="flex items-center justify-between text-xs text-emerald-900 font-medium tracking-wide">
                        <span data-testid="assessment-step-indicator">Step {step} of {TOTAL}</span>
                        <span className="flex items-center gap-1.5 text-emerald-700 font-semibold" data-testid="toolkit-mode-banner">
                            <FileCheck2 className="w-3.5 h-3.5" /> Toolkit purchased · No additional charge
                        </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-emerald-900/10 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-700 transition-all" style={{ width: `${(step / TOTAL) * 100}%` }} />
                    </div>
                </div>

                <div className="bg-white border border-emerald-900/10 rounded-2xl p-8 sm:p-10 shadow-sm">
                    {step === 1 && (
                        <Section
                            title="Confirm your contact information"
                            subtitle="We've pre-filled this from your annuity review. Correct anything that's changed, then continue."
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Full name" required>
                                    <input data-testid="field-full-name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} placeholder="Jane Smith" />
                                </Field>
                                <Field label="Phone" required>
                                    <input data-testid="field-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="(555) 555-5555" />
                                </Field>
                            </div>
                            <Field label="Email" required>
                                <input data-testid="field-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="jane@example.com" />
                            </Field>
                            <div className="grid sm:grid-cols-3 gap-5">
                                <Field label="Age range" required>
                                    <select data-testid="field-age" value={data.age} onChange={(e) => set("age", e.target.value)} className={inputCls}>
                                        <option value="">Select…</option>
                                        {AGE_RANGES.map((a) => <option key={a}>{a}</option>)}
                                    </select>
                                </Field>
                                <Field label="State" required>
                                    <select data-testid="field-state" value={data.state} onChange={(e) => set("state", e.target.value)} className={inputCls}>
                                        <option value="">Select…</option>
                                        {STATES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="ZIP" required>
                                    <input data-testid="field-zip" value={data.zip_code} onChange={(e) => set("zip_code", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} className={inputCls} placeholder="74103" />
                                </Field>
                            </div>
                            <p className="text-xs text-emerald-800 leading-relaxed pt-2">
                                Already accurate? Just click <strong>Continue</strong>.
                            </p>
                        </Section>
                    )}

                    {step === 2 && (
                        <Section
                            title="Retirement assessment questions"
                            subtitle="Five quick questions — about 3 minutes. Your printable scorecard arrives instantly."
                        >
                            <Choice label="Are you currently retired or planning to?" name="retirement_status" value={q.retirement_status} onChange={(v) => setQ("retirement_status", v)}
                                options={[
                                    { v: "retired", label: "I'm retired" },
                                    { v: "planning", label: "Planning to retire in 1–10 years" },
                                    { v: "working", label: "Still working, retirement is further out" },
                                ]} />
                            <Choice label="Do you currently own an annuity?" name="owns_annuity" value={q.owns_annuity} onChange={(v) => setQ("owns_annuity", v)}
                                options={[
                                    { v: "Yes", label: "Yes" },
                                    { v: "No", label: "No" },
                                    { v: "Not Sure", label: "Not Sure" },
                                ]} />
                            {q.owns_annuity === "Yes" && (
                                <Choice label="What type of annuity?" name="annuity_type" value={q.annuity_type} onChange={(v) => setQ("annuity_type", v)}
                                    options={[
                                        { v: "Variable", label: "Variable annuity" },
                                        { v: "Fixed Indexed", label: "Fixed indexed annuity (FIA)" },
                                        { v: "Fixed", label: "Fixed / MYGA annuity" },
                                        { v: "Not sure", label: "I'm not sure what type" },
                                    ]} />
                            )}
                            <Choice label="Total retirement savings (all accounts combined)" name="asset_range" value={q.asset_range} onChange={(v) => setQ("asset_range", v)} options={ASSET_RANGES} />
                            <Choice label="Do you have money in CDs or savings earning low rates?" name="owns_cd" value={q.owns_cd} onChange={(v) => setQ("owns_cd", v)}
                                options={[{ v: "Yes", label: "Yes" }, { v: "No", label: "No" }]} />
                            <Choice label="How confident are you in your retirement income?" name="income_confidence" value={q.income_confidence} onChange={(v) => setQ("income_confidence", v)}
                                options={CONFIDENCE.map((c) => ({ v: c, label: c }))} />
                            <Choice label="How worried are you about market downturns?" name="market_risk" value={q.market_risk} onChange={(v) => setQ("market_risk", v)}
                                options={RISK.map((r) => ({ v: r, label: r }))} />
                            <Choice label="Do you have a long-term care plan in place?" name="ltc_plan" value={q.ltc_plan} onChange={(v) => setQ("ltc_plan", v)} options={LTC_OPTIONS} />
                            <Choice label="Your #1 retirement priority" name="top_priority" value={q.top_priority} onChange={(v) => setQ("top_priority", v)} options={PRIORITIES} />

                            <div className="mt-2 p-5 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="toolkit-finalize-note">
                                <div className="flex items-start gap-3">
                                    <FileCheck2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-emerald-950 leading-relaxed">
                                        <strong>Toolkit purchased.</strong> Click <strong>Generate My Scorecard</strong> below to compute your score and email your printable report. <em>No additional charge.</em>
                                    </div>
                                </div>
                            </div>
                        </Section>
                    )}

                    <div className="mt-10 flex items-center justify-between gap-4 pt-6 border-t border-emerald-900/10">
                        <button
                            onClick={() => (step === 1 ? navigate(-1) : setStep(1))}
                            data-testid="assessment-back"
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-emerald-900 hover:text-emerald-900 text-sm font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        {step === 1 ? (
                            <button
                                disabled={!step1Valid}
                                onClick={() => setStep(2)}
                                data-testid="assessment-next"
                                className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 disabled:bg-emerald-900/30 disabled:cursor-not-allowed text-amber-300 px-6 py-3 rounded-lg font-medium text-sm transition-colors"
                            >
                                Continue <ArrowRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                disabled={!step2Valid || submitting}
                                onClick={onSubmit}
                                data-testid="assessment-generate-scorecard"
                                className="inline-flex items-center gap-2 bg-amber-300 hover:bg-amber-200 disabled:bg-amber-300/40 disabled:cursor-not-allowed text-emerald-950 px-6 py-3 rounded-lg font-bold text-sm shadow-md"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />}
                                {submitting ? "Generating your scorecard…" : "Generate My Scorecard"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Toolkit Downloads — available immediately, even before the assessment is completed */}
                {leadId && <ToolkitDownloads leadId={leadId} />}
            </div>
        </div>
    );
}
